import React from 'react';
import {
  Menu, SidebarClose, LogIn, UserPlus, LogOut,
  ShoppingCart, RotateCcw, Info
} from 'lucide-react';
import { getAnalytics, logEvent } from 'firebase/analytics';

// --- unchanged except TPT_STORE_LINK updated ---
const TPT_PURCHASE_LINK = "https://www.teacherspayteachers.com/Product/TPT-Seller-SEO-Listing-Optimizer-App-Create-Titles-Description-Product-Links-13645689?utm_source=Mr%20Joel%20Site%20&utm_campaign=Mr%20Joel%20Site";
const TPT_STORE_LINK = "https://www.teacherspayteachers.com/Product/TPT-Seller-SEO-Listing-Optimizer-App-Create-Titles-Descriptions-SEO-Ranking-13645689";
const SUPPORT_EMAIL = "info@mrjoelstptgenerator.com";

const Header = ({
  isSidebarOpen,
  setIsSidebarOpen,
  currentUser,
  isTrialActive,
  userAccessData,
  trialTimeRemaining,
  handleLogout,
  handleFullReset,
  triggerAuthModal,
}) => {

  const handlePurchaseClick = (location) => {
    try {
      const analytics = getAnalytics();
      logEvent(analytics, 'begin_checkout', {
        currency: 'USD',
        value: 15.00,
        items: [{
          item_id: 'TPT_SEO_APP_LIFETIME',
          item_name: 'TPT SEO Listing Optimizer Pro App - Lifetime Access'
        }],
        click_location: location
      });
    } catch (error) {
      console.error("Error logging purchase click to analytics:", error);
    }
  };

  const showPurchase =
    !currentUser ||
    (!!currentUser && userAccessData && !userAccessData.approvedByAdmin);

  return (
    <header className="mb-10 px-4">
      {/* Mobile Sidebar Toggle */}
      <div className="flex justify-between items-center mb-6">
        <button
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          className="md:hidden p-2 text-slate-600 hover:text-sky-600 fixed top-4 left-4 z-40 bg-white rounded-full shadow-lg"
          aria-label="Toggle sidebar"
        >
          {isSidebarOpen ? <SidebarClose size={24} /> : <Menu size={24} />}
        </button>

        <div className="flex-1"></div>

        <div className="flex items-center flex-wrap gap-2 justify-end ml-auto">
          {!currentUser ? (
            <>
              <button
                onClick={() => triggerAuthModal('Please log in to access all features.', 'login')}
                className="text-sm text-sky-600 hover:text-sky-800 font-medium flex items-center"
              >
                <LogIn size={16} className="mr-1" /> Login
              </button>
              <button
                onClick={() => triggerAuthModal('Sign up for a free trial!')}
                className="text-sm bg-green-500 hover:bg-green-600 text-white font-semibold py-1.5 px-3 rounded-full shadow-sm flex items-center"
              >
                <UserPlus size={16} className="mr-1" /> Sign Up
              </button>
            </>
          ) : (
            <button
              onClick={handleLogout}
              className="text-sm text-red-600 hover:text-red-800 font-medium flex items-center"
            >
              <LogOut size={16} className="mr-1" /> Logout
            </button>
          )}

          {showPurchase && (
            <a
              href={TPT_PURCHASE_LINK}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => handlePurchaseClick('header_button')}
              className="text-sm bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-white font-semibold py-1.5 px-4 rounded-full shadow-md flex items-center"
              title="Purchase Full Access on TPT"
            >
              <ShoppingCart size={16} className="mr-1" /> Purchase Full Access
            </a>
          )}

          <button
            onClick={handleFullReset}
            className="text-xs bg-slate-200 hover:bg-slate-300 text-slate-700 font-semibold py-1.5 px-3 rounded-full shadow-sm flex items-center"
            title="Reset all form fields and generated content"
          >
            <RotateCcw size={14} className="mr-1" /> Reset All
          </button>
        </div>
      </div>

      {/* Title & Subtitle */}
      <div className="text-center">
        <h1 className="text-4xl font-extrabold text-sky-700 tracking-tight">
          Mr Joel&apos;s TPT SEO Product Description Generator
        </h1>
        <p className="text-md text-slate-600 mt-2 max-w-2xl mx-auto">
          Instantly craft and refine your product descriptions, track your SEO keyword rankings, and generate all your social media content.
        </p>
      </div>

      {/* Guest Message */}
      {!currentUser && (
        <div className="mt-5 p-3 bg-blue-100 border border-blue-300 text-blue-700 rounded-lg text-sm text-center max-w-xl mx-auto">
          <p className="font-semibold">👋 Welcome, Guest!</p>
          <p>
            Please{' '}
            <button
              onClick={() => triggerAuthModal('Sign up for a free trial to generate content.')}
              className="underline font-semibold"
            >
              sign up for a free trial
            </button>{' '}
            or{' '}
            <button
              onClick={() => triggerAuthModal('Please log in to generate content.', 'login')}
              className="underline font-semibold"
            >
              log in
            </button>{' '}
            to enhance your TPT listings SEO.
          </p>
        </div>
      )}

      {/* Trial Mode Warning */}
      {currentUser && isTrialActive && !userAccessData?.approvedByAdmin && (
        <div className="mt-5 bg-indigo-100 border border-indigo-300 text-indigo-800 rounded-lg text-center p-5 max-w-xl mx-auto space-y-2">
          <p className="font-bold text-lg">🚀 Trial Mode Active</p>
          {trialTimeRemaining && <p>{trialTimeRemaining}</p>}
          <p>Generations Remaining: {Math.max(0, 10 - (userAccessData?.trialGenerationsUsed || 0))} / 10</p>
          <p>Product Saves Remaining (in &apos;Improve&apos;): {Math.max(0, 5 - (userAccessData?.trialProductSavesUsed || 0))} / 5</p>
          <div className="mt-4">
            <a
              href={TPT_PURCHASE_LINK}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => handlePurchaseClick('trial_banner_button')}
              className="inline-block bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white text-lg font-bold py-3 px-6 rounded-full shadow-md transition"
            >
              🔓 Upgrade to Full Access Now
            </a>
            <p className="mt-2 text-xs text-slate-600">Instant access once your TPT order is verified (6–12 hrs).</p>
          </div>
        </div>
      )}

      {/* Help and Store Links */}
      <div className="mt-6 text-sm text-slate-500 text-center">
        <span>
          Need help? Email:{' '}
          <a href={`mailto:${SUPPORT_EMAIL}`} className="text-sky-600 hover:underline">
            {SUPPORT_EMAIL}
          </a>
        </span>
        <span className="mx-2">|</span>
        <span>
          Visit my store:{' '}
          <a href={TPT_STORE_LINK} target="_blank" rel="noopener noreferrer" className="text-sky-600 hover:underline">
            Mr Joel&apos;s Math &amp; Science Store
          </a>
        </span>
      </div>

      {/* Chrome Extension Announcement Banner */}
      <div
        className="mt-4 max-w-xl mx-auto px-4 py-3 rounded-lg bg-sky-50 text-sky-900 border border-sky-200 border-l-4 border-l-sky-400 shadow-sm text-center"
        role="note"
        aria-live="polite"
      >
        <p className="text-sm leading-6">
          <span className="inline-flex items-center justify-center gap-2 font-semibold">
            <Info size={16} className="shrink-0" />
            New! Chrome Extension
          </span>
          <span className="block mt-1">
            The Chrome Extension is now live!{' '}
            <a
              href="https://chromewebstore.google.com/detail/mr-joels-seller-tools-seo/neplkgjelpnenikeeampeiakadihknbc?utm_source=item-share-cb"
              target="_blank"
              rel="noopener noreferrer"
              className="underline text-sky-700 font-bold"
            >
              Click here to download.
            </a>
          </span>
        </p>
      </div>
    </header>
  );
};

export default Header;